import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import type {
  RTLSState,
  RTLSAsset,
  FilterType,
  FilterStatus,
  FilterLevel,
  HeatmapType,
  LocationData,
  AccessPoint,
  PanelEntityFilter,
  RescueChamber,
} from '../types/rtlsTypes';

const initialState: RTLSState = {
  assets: {},
  selectedAssetId: null,
  filterType: 'all',
  filterStatus: 'all',
  filterLevel: 'all',
  panelFilter: 'all',
  connectionStatus: 'disconnected',
  lastBatchUpdate: 0,
  pollingInterval: 3000,
  // Trail / History
  positionHistory: {},
  loadedTrailAssets: [],
  showTrails: false,
  trailLength: 200,
  playbackMode: 'live',
  playbackTimestamp: null,
  playbackSpeed: 50,
  playbackPlaying: false,
  // Heatmap
  heatmapEnabled: false,
  heatmapType: 'density',
  // Locations
  locations: {},
  // Access points
  accessPoints: {},
  // Rescue chambers — TagBoard-owned, RTLS-rendered as 3D safety markers
  rescueChambers: {},
};

const rtlsSlice = createSlice({
  name: 'rtls',
  initialState,
  reducers: {
    updatePositions(state, action: PayloadAction<RTLSAsset[]>) {
      const now = Date.now();
      for (const asset of action.payload) {
        const existing = state.assets[asset.id];
        state.assets[asset.id] = {
          ...asset,
          previousPosition: existing?.position,
          // Preserve caller-supplied lastUpdated (e.g., Supabase DB value)
          // so the assets list can sort by true recency. Fall back to `now`
          // only when the caller didn't provide one.
          lastUpdated: asset.lastUpdated ?? now,
        };
        // Skip accumulating live position history for assets with pre-loaded trail history
        if (!state.loadedTrailAssets.includes(asset.id)) {
          if (!state.positionHistory[asset.id]) {
            state.positionHistory[asset.id] = [];
          }
          const history = state.positionHistory[asset.id];
          const lastEntry = history.length > 0 ? history[history.length - 1] : null;
          const minDistance = 3; // Skip movements smaller than 3 units
          let shouldAdd = true;
          if (lastEntry) {
            const dx = asset.position.x - lastEntry.position.x;
            const dy = asset.position.y - lastEntry.position.y;
            const dz = asset.position.z - lastEntry.position.z;
            if (Math.sqrt(dx * dx + dy * dy + dz * dz) < minDistance) {
              shouldAdd = false;
            }
          }
          if (shouldAdd) {
            history.push({
              position: {...asset.position},
              timestamp: now,
            });
            // Trim to trailLength
            if (history.length > state.trailLength) {
              state.positionHistory[asset.id] = history.slice(-state.trailLength);
            }
          }
        }
      }
      state.lastBatchUpdate = now;
    },
    removeAsset(state, action: PayloadAction<string>) {
      delete state.assets[action.payload];
      if (state.selectedAssetId === action.payload) {
        state.selectedAssetId = null;
      }
    },
    selectAsset(state, action: PayloadAction<string | null>) {
      state.selectedAssetId = action.payload;
    },
    clearSelection(state) {
      state.selectedAssetId = null;
    },
    setFilterType(state, action: PayloadAction<FilterType>) {
      state.filterType = action.payload;
    },
    setFilterStatus(state, action: PayloadAction<FilterStatus>) {
      state.filterStatus = action.payload;
    },
    setFilterLevel(state, action: PayloadAction<FilterLevel>) {
      state.filterLevel = action.payload;
    },
    setPanelFilter(state, action: PayloadAction<PanelEntityFilter>) {
      state.panelFilter = action.payload;
    },
    setConnectionStatus(
      state,
      action: PayloadAction<RTLSState['connectionStatus']>,
    ) {
      state.connectionStatus = action.payload;
    },
    setPollingInterval(state, action: PayloadAction<number>) {
      state.pollingInterval = action.payload;
    },
    clearAllAssets(state) {
      state.assets = {};
      state.selectedAssetId = null;
      state.lastBatchUpdate = 0;
    },
    // ─── Trail / History reducers ───────────────────
    toggleTrails(state) {
      state.showTrails = !state.showTrails;
    },
    setTrailLength(state, action: PayloadAction<number>) {
      state.trailLength = action.payload;
      // Trim existing histories to new length
      for (const id of Object.keys(state.positionHistory)) {
        if (state.positionHistory[id].length > action.payload) {
          state.positionHistory[id] =
            state.positionHistory[id].slice(-action.payload);
        }
      }
    },
    setPlaybackMode(state, action: PayloadAction<'live' | 'playback'>) {
      state.playbackMode = action.payload;
      if (action.payload === 'live') {
        state.playbackTimestamp = null;
        state.playbackPlaying = false;
      }
    },
    setPlaybackPlaying(state, action: PayloadAction<boolean>) {
      state.playbackPlaying = action.payload;
    },
    setPlaybackSpeed(state, action: PayloadAction<number>) {
      // Clamp to the UI stepper range so out-of-range inputs can't poison state.
      state.playbackSpeed = Math.max(1, Math.min(50, Math.round(action.payload)));
    },
    setPlaybackTimestamp(state, action: PayloadAction<number | null>) {
      state.playbackTimestamp = action.payload;
    },
    clearHistory(state) {
      state.positionHistory = {};
    },
    loadTrailHistory(
      state,
      action: PayloadAction<Record<string, {position: {x: number; y: number; z: number}; timestamp: number}[]>>,
    ) {
      for (const [assetId, entries] of Object.entries(action.payload)) {
        state.positionHistory[assetId] = entries.map(e => ({
          position: e.position,
          timestamp: e.timestamp,
        }));
        if (!state.loadedTrailAssets.includes(assetId)) {
          state.loadedTrailAssets.push(assetId);
        }
      }
    },
    // ─── Heatmap reducers ───────────────────────────
    toggleHeatmap(state) {
      state.heatmapEnabled = !state.heatmapEnabled;
    },
    setHeatmapType(state, action: PayloadAction<HeatmapType>) {
      state.heatmapType = action.payload;
    },
    // ─── Location reducers ──────────────────────────
    addLocation(state, action: PayloadAction<LocationData>) {
      state.locations[action.payload.id] = action.payload;
    },
    removeLocation(state, action: PayloadAction<string>) {
      delete state.locations[action.payload];
    },
    setLocations(state, action: PayloadAction<LocationData[]>) {
      state.locations = {};
      for (const loc of action.payload) {
        state.locations[loc.id] = loc;
      }
    },
    updateLocation(state, action: PayloadAction<LocationData>) {
      // Guard: do not re-create a row that was deleted between the dialog
      // open and submit. A no-op in that case is preferable to a resurrected
      // orphan in Redux.
      if (state.locations[action.payload.id]) {
        state.locations[action.payload.id] = action.payload;
      }
    },
    // ─── Access point reducers ──────────────────────
    addAccessPoint(state, action: PayloadAction<AccessPoint>) {
      state.accessPoints[action.payload.id] = action.payload;
    },
    removeAccessPoint(state, action: PayloadAction<string>) {
      delete state.accessPoints[action.payload];
    },
    setAccessPoints(state, action: PayloadAction<AccessPoint[]>) {
      state.accessPoints = {};
      for (const ap of action.payload) {
        state.accessPoints[ap.id] = ap;
      }
    },
    updateAccessPoint(state, action: PayloadAction<AccessPoint>) {
      if (state.accessPoints[action.payload.id]) {
        state.accessPoints[action.payload.id] = action.payload;
      }
    },
    // Used by the realtime subscription — handles both INSERT and UPDATE
    // payloads in one reducer (no need to check existence first).
    upsertAccessPoint(state, action: PayloadAction<AccessPoint>) {
      state.accessPoints[action.payload.id] = action.payload;
    },
    // ─── Rescue chamber reducers ────────────────────
    setRescueChambers(state, action: PayloadAction<RescueChamber[]>) {
      state.rescueChambers = {};
      for (const c of action.payload) {
        state.rescueChambers[c.id] = c;
      }
    },
    upsertRescueChamber(state, action: PayloadAction<RescueChamber>) {
      state.rescueChambers[action.payload.id] = action.payload;
    },
    removeRescueChamber(state, action: PayloadAction<string>) {
      delete state.rescueChambers[action.payload];
    },
    // Live-occupancy adjustment driven by chamber_personnel INSERT/DELETE
    // realtime events. The rescue_chambers row's current_occupancy column
    // may lag the join table, so we mutate it locally to keep marker
    // tooltips + capacity bars accurate without waiting for TagBoard to
    // re-write the chamber row.
    incrementChamberOccupancy(
      state,
      action: PayloadAction<{chamberId: string; delta: number}>,
    ) {
      const c = state.rescueChambers[action.payload.chamberId];
      if (!c) return;
      const next = Math.max(0, (c.currentOccupancy ?? 0) + action.payload.delta);
      const capped =
        typeof c.capacity === 'number' ? Math.min(next, c.capacity) : next;
      c.currentOccupancy = capped;
    },
    // ─── Single-asset update (used by personnel/equipment realtime subs) ──
    // updatePositions already handles upsert-by-id for arrays; this is a
    // convenience wrapper for the postgres_changes UPDATE event which
    // delivers one row at a time. Bypasses the position-history accumulator
    // because realtime UPDATE events fire on any column change (status,
    // shift, etc.) — we shouldn't log a trail point for non-spatial edits.
    updateSingleAsset(state, action: PayloadAction<RTLSAsset>) {
      const asset = action.payload;
      const existing = state.assets[asset.id];
      state.assets[asset.id] = {
        ...asset,
        previousPosition: existing?.position,
        lastUpdated: asset.lastUpdated ?? Date.now(),
      };
    },
  },
});

export const rtlsActions = rtlsSlice.actions;
export default rtlsSlice.reducer;
